"use client"
import { useNavigate } from "react-router-dom";

import { type FC, useRef } from "react"

export interface SectionHeroProps {
  className?: string
  backgroundImage?: string
}

const SectionHero: FC<SectionHeroProps> = ({ className = "" }) => {
  const heroRef = useRef<HTMLDivElement>(null)
  const navigate = useNavigate()

  const handleStartJourney = () => {
    if (heroRef.current) {
      const heroBottom = heroRef.current.offsetHeight
      window.scrollTo({
        top: heroBottom,
        behavior: "smooth",
      })
    }
  }

  const handleLearnMore = () => {
    navigate("/about")
  }

  return (
    <div
      ref={heroRef}
      className={`relative w-full overflow-hidden dashboard-bg ${className}`}
      data-nc-id="SectionHero"
    >
      <div className="absolute inset-0 "></div>

      {/* Gold luxury decorative elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute -top-40 -right-40 w-80 h-80 bg-yellow-500/8 rounded-full blur-3xl"></div>
        <div className="absolute -bottom-20 -left-40 w-96 h-96 bg-yellow-600/8 rounded-full blur-3xl"></div>

        {/* Gold divider lines */}
        <div className="absolute top-20 left-0 right-0 h-px bg-gradient-to-r from-transparent via-yellow-400/30 to-transparent"></div>
        <div className="absolute bottom-32 left-0 right-0 h-px bg-gradient-to-r from-transparent via-yellow-400/30 to-transparent"></div>

        <div className="absolute top-0 left-0 w-32 h-32 border-t border-l border-yellow-400/20"></div>
        <div className="absolute bottom-0 right-0 w-32 h-32 border-b border-r border-yellow-400/20"></div>
      </div>

      {/* Main content container */}
      <div className="relative z-10 mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-16 md:py-24 lg:py-32 min-h-screen flex items-center">
        <div className="w-full max-w-7xl mx-auto">
          <div className="grid grid-cols-1 gap-8 lg:gap-16 items-center">
            {/* Left section - Main heading and CTA */}
            <div className="flex flex-col items-center justify-center text-center space-y-6 sm:space-y-8">
              <div className="inline-flex items-center gap-2 px-5 py-2.5 bg-yellow-500/15 rounded-full border-2 border-yellow-400/50 backdrop-blur-sm shadow-lg">
                <span className="w-2.5 h-2.5 bg-yellow-300 rounded-full shadow-lg"></span>
                <span className="text-xs sm:text-sm font-semibold text-yellow-200 tracking-widest">
                  ✦ EXPLORE EUROPE ✦
                </span>
              </div>

              <div className="relative">
                <div className="absolute inset-0 blur-2xl bg-yellow-500/15 rounded-full"></div>
                <h1 className="relative text-4xl sm:text-5xl md:text-6xl lg:text-7xl font-bold text-white leading-tight tracking-tight text-balance drop-shadow-lg">
                  EUROPE TOURS & TRAVELS
                </h1>
              </div>

              <div className="flex items-center gap-4 justify-center">
                {/* <div className="h-0.5 w-16 bg-gradient-to-r from-yellow-500/0 via-yellow-400 to-yellow-500/0"></div>
                <span className="text-2xl text-yellow-300 drop-shadow-md">✦</span>
                <div className="h-0.5 w-16 bg-gradient-to-l from-yellow-500/0 via-yellow-400 to-yellow-500/0"></div> */}
              </div>

              {/* Subheading */}
              <p className="text-base sm:text-lg md:text-xl text-white/90 max-w-xl leading-relaxed drop-shadow-md font-medium">
                We have built our company based on strong connections with travel agents throughout the world.
              </p>

              <div className="flex flex-col sm:flex-row gap-4 pt-6">
                <button
                  onClick={handleStartJourney}
                  className="relative px-8 sm:px-10 py-4 sm:py-5 bg-gradient-to-r from-yellow-300 via-yellow-400 to-yellow-500 hover:from-yellow-200 hover:via-yellow-300 hover:to-yellow-400 text-slate-900 font-bold rounded-lg transition-all duration-300 transform hover:scale-105 active:scale-95 shadow-2xl hover:shadow-yellow-500/50 overflow-hidden text-lg"
                >
                  <span className="relative z-10">Start Your Journey </span>
                </button>
                <button
                  onClick={handleLearnMore}
                  className="px-8 sm:px-10 py-4 sm:py-5 border-2 border-yellow-300 text-yellow-100 hover:bg-yellow-500/20 font-bold rounded-lg transition-all duration-300 backdrop-blur-md shadow-lg hover:shadow-yellow-400/30 text-lg"
                >
                  Learn More
                </button>
              </div>

              <div className="pt-12 flex items-center gap-4">
                <div className="h-px flex-1 bg-gradient-to-r from-transparent via-yellow-400/40 to-transparent"></div>
                <span className="text-yellow-200/80 text-sm font-semibold tracking-wide drop-shadow-md">
                  Your Europe Travel Specialists
                </span>
                <div className="h-px flex-1 bg-gradient-to-l from-transparent via-yellow-400/40 to-transparent"></div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Scroll indicator */}
      <div className="absolute bottom-8 left-1/2 transform -translate-x-1/2 z-20">
        <div className="flex flex-col items-center gap-2 text-yellow-200/70 animate-bounce">
          <span className="text-xs sm:text-sm font-semibold drop-shadow-md">Scroll to explore</span>
          <svg className="w-5 h-5 sm:w-6 sm:h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 14l-7 7m0 0l-7-7m7 7V3" />
          </svg>
        </div>
      </div>
    </div>
  )
}

export default SectionHero
